import { BillType } from "@redotech/cockroachdb-codegen/tables/billing/bill-type";

/** Minimal bill shape the billed-date derivation needs, so both the CRDB row
 * and the RPC schema can be passed without a mapping layer. */
export interface BilledDateBill {
  type: BillType;
  endTime: Date | string;
  billingDueDate?: Date | string | null;
}

/** The date a bill is charged to the merchant — the "Billed" anchor the Bills
 * list shows. Stored due date when present, otherwise the period close. */
export function getBilledDate(bill: BilledDateBill): Date {
  if (bill.billingDueDate) {
    return new Date(bill.billingDueDate);
  }
  const endTime = new Date(bill.endTime);
  // A returns period ends the instant BEFORE its close day, and the merchant is
  // charged on that close day — showing end_time reads a day early.
  return bill.type === BillType.ReturnShipping
    ? new Date(endTime.getTime() + 1)
    : endTime;
}

/** Returns close boundaries are anchored on UTC midnight, so the billed day has
 * to be read in UTC; a merchant west of UTC would otherwise see the day before. */
export function getBilledDateTimezone(
  bill: BilledDateBill,
  ianaTimezone: string | undefined,
): string {
  if (!bill.billingDueDate && bill.type === BillType.ReturnShipping) {
    return "UTC";
  }
  return ianaTimezone ?? "UTC";
}

/** The billed date as a calendar day, read in the same zone the Bills page
 * uses, so a "next invoice" label can never disagree with the Bills row. */
export function getBilledPlainDate(
  bill: BilledDateBill,
  ianaTimezone: string | undefined,
): Temporal.PlainDate {
  return Temporal.Instant.fromEpochMilliseconds(getBilledDate(bill).getTime())
    .toZonedDateTimeISO(getBilledDateTimezone(bill, ianaTimezone))
    .toPlainDate();
}
